// Copyright (c) 2026. Hardfin, Inc. All rights reserved.
// Hardfin Confidential - Restricted
import { EmptyState } from '@/components/empty-state';
import {
  ChartData,
  TooltipData,
  createDataDatumAttribute,
  createDirectFlexibleTooltipMouseMoveHandler,
  createFlexibleTooltipMouseMoveHandler,
  formatMinutesToFractionalHours,
} from '@/costs/components/chart-tooltip-helpers';
import { IsoCurrencyCode } from '@/currency/constants/iso';
import { formatCurrency, formatNumber } from '@/utilities/number';
import { Box, Typography, useTheme } from '@mui/material';
import { AxisLeft } from '@visx/axis';
import { scaleBand, scaleLinear } from '@visx/scale';
import { Text } from '@visx/text';
import { defaultStyles, useTooltip, useTooltipInPortal } from '@visx/tooltip';
import { useEffect, useRef, useState } from 'react';

interface VerticalBarChartProps {
  data: ChartData[];
  loading?: boolean;
  displayMode?: 'currency' | 'time';
  /** Currency the values are denominated in (currency mode only). */
  currencyCode?: IsoCurrencyCode;
}

const MIN_BAR_WIDTH = 40;
const MAX_BAR_WIDTH = 80;
const BAR_SPACING = 5;
const MARGIN = { top: 20, right: 40, bottom: 100, left: 80 };
const TICK_LENGTH = 5;

const formatCurrencyAbbreviated = (
  value: number,
  currencyCode?: IsoCurrencyCode
): string => {
  return formatCurrency(value, {
    abbreviate: true,
    digits: 1,
    hideZeroDecimals: true,
    currencyCode,
  });
};

const formatTimeAbbreviated = (minutes: number): string => {
  return formatMinutesToFractionalHours(minutes);
};

const formatCurrencyAxis = (
  value: number,
  currencyCode?: IsoCurrencyCode
): string => {
  return formatCurrency(value, {
    abbreviate: true,
    digits: 1,
    hideZeroDecimals: true,
    currencyCode,
  });
};

const formatTimeAxis = (minutes: number): string => {
  return formatMinutesToFractionalHours(minutes);
};

export function VerticalBarChart(props: VerticalBarChartProps) {
  const {
    data,
    loading = false,
    displayMode = 'currency',
    currencyCode,
  } = props;
  const theme = useTheme();
  const containerRef = useRef<HTMLDivElement>(null);
  const scrollableRef = useRef<HTMLDivElement>(null);
  const firstLabelRef = useRef<SVGTextElement>(null);
  const [containerWidth, setContainerWidth] = useState(800);
  const [scrollLeft, setScrollLeft] = useState(0);

  const {
    tooltipData,
    tooltipLeft,
    tooltipTop,
    tooltipOpen,
    showTooltip,
    hideTooltip,
  } = useTooltip<TooltipData>();

  const { containerRef: tooltipContainerRef, TooltipInPortal } =
    useTooltipInPortal({
      detectBounds: true,
      scroll: true,
    });

  // Update width when container resizes
  useEffect(() => {
    const updateWidth = () => {
      if (containerRef.current) {
        const width = containerRef.current.getBoundingClientRect().width;
        setContainerWidth(width);
      }
    };

    // Delay initial width measurement to allow for expand animations
    const timeoutId = setTimeout(updateWidth, 200);

    const observer = new ResizeObserver(updateWidth);
    if (containerRef.current) {
      observer.observe(containerRef.current);
    }

    return () => {
      clearTimeout(timeoutId);
      observer.disconnect();
    };
  }, []);

  // Track scroll position for first label visibility
  useEffect(() => {
    const handleScroll = () => {
      if (scrollableRef.current) {
        setScrollLeft(scrollableRef.current.scrollLeft);
      }
    };

    const scrollElement = scrollableRef.current;
    if (scrollElement) {
      scrollElement.addEventListener('scroll', handleScroll);
      return () => scrollElement.removeEventListener('scroll', handleScroll);
    }
  }, []);

  const handleMouseMove = createFlexibleTooltipMouseMoveHandler(
    showTooltip,
    displayMode,
    currencyCode
  );

  if (loading) {
    return (
      <Typography color="text.secondary" textAlign="center" py={4}>
        Loading...
      </Typography>
    );
  }

  if (data.length === 0) {
    return (
      <EmptyState
        title="No data available"
        message="Try adjusting your filters or date range to see data"
      />
    );
  }

  // Chart dimensions - fill container width or scroll if too many bars
  const minRequiredWidth =
    data.length * (MIN_BAR_WIDTH + BAR_SPACING) + MARGIN.left + MARGIN.right;
  const width = Math.max(containerWidth, minRequiredWidth);
  const height = 400;
  const innerWidth = width - MARGIN.left - MARGIN.right;
  const innerHeight = height - MARGIN.top - MARGIN.bottom;

  // Calculate appropriate upper bound for the domain using "nice" intervals
  const maxValue = Math.max(...data.map((d) => d.value), 0);
  const getNiceUpperBound = (value: number): number => {
    if (value === 0) return 100;

    // Find the NEXT power of 10 above the value, then go one level down
    const nextPowerOfTen = Math.pow(10, Math.ceil(Math.log10(value)));
    const incrementPowerOfTen = nextPowerOfTen / 10;

    // Round up to the next increment of that power of 10
    const result = Math.ceil(value / incrementPowerOfTen) * incrementPowerOfTen;
    return result;
  };

  // Scales
  const xScale = scaleBand({
    domain: data.map((d) => d.label),
    range: [0, innerWidth],
    padding: 0.1,
  });

  const yScale = scaleLinear({
    domain: [0, getNiceUpperBound(maxValue)],
    range: [innerHeight, 0],
  });

  // Calculate floating label opacity and position with smooth fade-out
  const firstBarX = data.length > 0 ? (xScale(data[0].label) ?? 0) : 0;
  const firstBarWidth = xScale.bandwidth();
  const firstBarCenterX = firstBarX + firstBarWidth / 2;

  // Calculate fade-out opacity based on how much of the bar has scrolled out
  const fadeOutOpacity =
    data.length > 0
      ? (() => {
          // Start fading when scroll begins, complete fade 15px before bar is fully scrolled out
          const fadeStart = 0;
          const fadeEnd = firstBarX + firstBarWidth - 15;

          if (scrollLeft <= fadeStart) return 1; // Fully visible
          if (scrollLeft >= fadeEnd) return 0; // Fully hidden

          // More aggressive fade using quadratic easing
          const progress = (scrollLeft - fadeStart) / (fadeEnd - fadeStart);
          return 1 - progress * progress; // Quadratic fade-out
        })()
      : 0;

  const showFloatingFirstLabel = data.length > 0 && fadeOutOpacity > 0;

  // Position floating label exactly where original would be, accounting for scroll
  const floatingLabelPosition = {
    x: MARGIN.left + firstBarCenterX - scrollLeft,
    y: MARGIN.top + innerHeight + TICK_LENGTH,
  };

  return (
    <Box
      ref={containerRef}
      sx={{
        width: '100%',
        display: 'flex',
        position: 'relative',
      }}
    >
      {/* Fixed Y-axis */}
      <Box sx={{ flexShrink: 0 }}>
        <svg width={MARGIN.left} height={height} style={{ display: 'block' }}>
          <AxisLeft
            scale={yScale}
            left={MARGIN.left}
            top={MARGIN.top}
            stroke={theme.palette.divider}
            tickLength={TICK_LENGTH}
            tickStroke={theme.palette.divider}
            numTicks={14}
            tickLabelProps={() => ({
              fill: theme.palette.text.secondary,
              fontSize: 14,
              textAnchor: 'end',
              verticalAnchor: 'middle',
            })}
            tickFormat={(value) =>
              displayMode === 'currency'
                ? formatCurrencyAxis(Number(value), currencyCode)
                : formatTimeAxis(Number(value))
            }
          />
        </svg>
      </Box>

      {/* Scrollable chart area */}
      <Box ref={scrollableRef} sx={{ overflow: 'auto', flexGrow: 1 }}>
        <svg
          ref={tooltipContainerRef}
          width={width - MARGIN.left}
          height={height}
          style={{ display: 'block' }}
        >
          {/* X-axis labels */}
          <g
            transform={`translate(0, ${MARGIN.top + innerHeight + TICK_LENGTH})`}
          >
            {data.map((datum, index) => {
              const barX = xScale(datum.label) ?? 0;
              const scaledWidth = xScale.bandwidth();
              const labelX = barX + scaledWidth / 2;

              const isFirstLabel = index === 0;

              return (
                <text
                  key={`label-${index}`}
                  ref={isFirstLabel ? firstLabelRef : undefined}
                  x={labelX}
                  y={15}
                  fill={theme.palette.text.secondary}
                  fontSize={14}
                  textAnchor="end"
                  transform={`rotate(-45, ${labelX}, 15)`}
                  style={{
                    userSelect: 'none',
                    opacity: isFirstLabel && showFloatingFirstLabel ? 0 : 1,
                  }}
                >
                  {datum.label}
                </text>
              );
            })}
          </g>

          {/* X-axis line */}
          <line
            x1={0}
            x2={innerWidth}
            y1={MARGIN.top + innerHeight}
            y2={MARGIN.top + innerHeight}
            stroke={theme.palette.divider}
            strokeWidth={1}
          />

          {/* Horizontal grid lines extending through the chart */}
          <g transform={`translate(0, ${MARGIN.top})`}>
            {yScale.ticks().map((tickValue) => {
              const y = yScale(tickValue);
              return (
                <line
                  key={`grid-${tickValue}`}
                  x1={0}
                  x2={innerWidth}
                  y1={y}
                  y2={y}
                  stroke={theme.palette.text.secondary}
                  strokeWidth={1}
                  opacity={0.2}
                />
              );
            })}
          </g>

          {/* Vertical Bars */}
          <g transform={`translate(0, ${MARGIN.top})`}>
            {data.map((datum, index) => {
              const scaledX = xScale(datum.label) ?? 0;
              const scaledWidth = xScale.bandwidth();
              const barWidth = Math.max(
                MIN_BAR_WIDTH,
                Math.min(MAX_BAR_WIDTH, scaledWidth)
              );
              const barX = scaledX + (scaledWidth - barWidth) / 2;
              const barHeight = Math.max(1, innerHeight - yScale(datum.value)); // Minimum 1px height
              const barY =
                datum.value === 0 ? innerHeight - 1 : yScale(datum.value); // Position zero bars at bottom

              return (
                <g key={`bar-${index}`}>
                  <rect
                    x={barX}
                    y={barY}
                    width={barWidth}
                    height={barHeight}
                    fill={theme.palette.primary.main}
                    data-datum={createDataDatumAttribute(datum)}
                    onMouseMove={handleMouseMove}
                    onMouseLeave={hideTooltip}
                    style={{ cursor: 'pointer' }}
                  />
                  {(() => {
                    const formattedValue =
                      displayMode === 'currency'
                        ? formatCurrencyAbbreviated(datum.value, currencyCode)
                        : formatTimeAbbreviated(datum.value);
                    // Estimate text width (rough approximation: 6px per character for 10px font)
                    const textWidth = formattedValue.length * 6;
                    // Calculate offsets using radial math for -45° rotation
                    const angle = -45 * (Math.PI / 180); // Convert to radians
                    const xOffset = textWidth * Math.cos(angle) * 0.5; // X component of diagonal
                    const yOffset = textWidth * Math.sin(angle) * 0.5; // Y component of diagonal

                    // Calculate proposed label position for rotated text
                    const rotatedLabelX = barX + barWidth / 2 + 10 + xOffset;
                    const rotatedLabelY = barY - 20 - Math.abs(yOffset);

                    // Calculate the rotated bounding box top-left corner
                    const rotatedTopY =
                      rotatedLabelY - textWidth * Math.sin(Math.abs(angle));

                    // Check if the rotated text would go above the chart area (y=0 in chart coordinates)
                    const wouldOverflow = rotatedTopY < 0;

                    if (wouldOverflow) {
                      // Fallback to horizontal text at top of bar
                      const textWidth = formattedValue.length * 6;
                      const textHeight = 12;
                      const rectX = barX + barWidth / 2 - textWidth / 2 - 2;
                      const rectY = barY - 8 - textHeight - 2;

                      return (
                        <g>
                          <rect
                            x={rectX}
                            y={rectY}
                            width={textWidth + 4}
                            height={textHeight + 4}
                            fill="rgba(255, 255, 255, 0.75)"
                            rx={2}
                            ry={2}
                          />
                          <Text
                            x={barX + barWidth / 2}
                            y={barY - 8}
                            verticalAnchor="end"
                            textAnchor="middle"
                            fill={theme.palette.text.primary}
                            fontSize={10}
                            fontWeight="bold"
                            style={{ cursor: 'pointer' }}
                            onMouseMove={createDirectFlexibleTooltipMouseMoveHandler(
                              showTooltip,
                              datum,
                              displayMode,
                              currencyCode
                            )}
                            onMouseLeave={hideTooltip}
                          >
                            {formattedValue}
                          </Text>
                        </g>
                      );
                    }

                    // Normal rotated text
                    const rotatedTextWidth = formattedValue.length * 6;
                    const rotatedTextHeight = 12;
                    // For rotated text, we need to position the background rect properly
                    const rectWidth = rotatedTextWidth + 4;
                    const rectHeight = rotatedTextHeight + 4;
                    const rectX = rotatedLabelX - rectWidth + 2;
                    const rectY = rotatedLabelY - rectHeight + 2;

                    return (
                      <g>
                        <rect
                          x={rectX}
                          y={rectY}
                          width={rectWidth}
                          height={rectHeight}
                          fill="rgba(255, 255, 255, 0.75)"
                          rx={2}
                          ry={2}
                          transform={`rotate(-45, ${rotatedLabelX}, ${rotatedLabelY})`}
                        />
                        <Text
                          x={rotatedLabelX}
                          y={rotatedLabelY}
                          verticalAnchor="end"
                          textAnchor="end"
                          fill={theme.palette.text.primary}
                          fontSize={10}
                          fontWeight="bold"
                          transform={`rotate(-45, ${rotatedLabelX}, ${rotatedLabelY})`}
                          style={{ cursor: 'pointer' }}
                          onMouseMove={createDirectFlexibleTooltipMouseMoveHandler(
                            showTooltip,
                            datum,
                            displayMode,
                            currencyCode
                          )}
                          onMouseLeave={hideTooltip}
                        >
                          {formattedValue}
                        </Text>
                      </g>
                    );
                  })()}
                </g>
              );
            })}
          </g>
        </svg>
      </Box>

      {/* Floating first label that can bleed over Y-axis */}
      {showFloatingFirstLabel && data.length > 0 && (
        <Box
          sx={{
            position: 'absolute',
            left: floatingLabelPosition.x,
            top: floatingLabelPosition.y,
            pointerEvents: 'none',
            zIndex: 1000,
          }}
        >
          <svg
            width="150"
            height="50"
            style={{
              overflow: 'visible',
              opacity: fadeOutOpacity,
              transition: 'opacity 0.1s ease-out',
            }}
          >
            <text
              x={0}
              y={15}
              fill={theme.palette.text.secondary}
              fontSize={14}
              textAnchor="end"
              transform={`rotate(-45, 0, 15)`}
              style={{ userSelect: 'none' }}
            >
              {data[0].label}
            </text>
          </svg>
        </Box>
      )}

      {/* Tooltip */}
      {tooltipOpen && tooltipData && (
        <TooltipInPortal
          top={tooltipTop}
          left={tooltipLeft}
          style={{
            ...defaultStyles,
            backgroundColor: theme.palette.background.paper,
            color: theme.palette.text.primary,
            border: `1px solid ${theme.palette.divider}`,
          }}
        >
          <Box sx={{ p: 1, minWidth: 160 }}>
            <Typography variant="subtitle2" textAlign="center" sx={{ mb: 1 }}>
              {tooltipData.label}
            </Typography>
            <Box
              sx={{
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center',
              }}
            >
              <Typography variant="body2" color="text.secondary">
                Count:
              </Typography>
              <Typography variant="body2">
                {formatNumber(tooltipData.count)}
              </Typography>
            </Box>
            <Box
              sx={{
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center',
              }}
            >
              <Typography variant="body2" color="text.secondary">
                Total:
              </Typography>
              <Typography variant="body2">
                {tooltipData.formattedValue}
              </Typography>
            </Box>
          </Box>
        </TooltipInPortal>
      )}
    </Box>
  );
}
